import { i18n } from "@tellescope/i18n";

/**
 * Represents a name with optional first and last name components.
 */
export interface Name {
  /** The person's first name */
  fname?: string;
  /** The person's last name */
  lname?: string;
  displayName?: string;
  internalDisplayName?: string;
}

/**
 * Formats a name object into a full name string.
 *
 * Combines first name and last name with a space separator, handling cases where
 * either or both names may be undefined or empty. The result is trimmed to remove
 * any leading or trailing whitespace.
 *
 * @template T - A type that extends the Name interface
 * @param name - An object containing optional fname and lname properties
 * @returns A formatted string with first and last name, or an empty string if both are missing
 *
 * @example
 * ```ts
 * formatName({ fname: "John", lname: "Doe" })
 * // Returns: "John Doe"
 *
 * formatName({ fname: "Jane" })
 * // Returns: "Jane"
 *
 * formatName({ lname: "Smith" })
 * // Returns: "Smith"
 *
 * formatName({})
 * // Returns: ""
 * ```
 */
export const formatName = <T extends Name>(name: T, internal?: boolean): string => {
  if (internal && name.internalDisplayName) return name.internalDisplayName;
  if (name.displayName) return name.displayName;
  return `${name.fname ?? ""} ${name.lname ?? ""}`.trim();
};

export const formatInitials = <T extends Name | string>(name: T, maxCount: number = 2): string => {
  const str = typeof name === "string" ? name : formatName(name);
  return str.split(" ").reduce<string>((acc, curr) => {
    if (acc.length >= maxCount) return acc;
    acc = acc + curr.at(0);
    return acc;
  }, "");
};

export const formatNames = <T extends Name>(names: T[], maxCount = 2, internal?: boolean): string => {
  if (names.length === 0) return "";
  const firstName = names[0];
  if (names.length === 1) return firstName ? formatName(firstName) : "";

  if (names.length <= maxCount) {
    const allButFinalNames = names
      .slice(0, -1)
      .map((name) => formatName(name, internal))
      .join(", ");
    const lastEntry = names[names.length - 1];
    const finalName = lastEntry ? formatName(lastEntry) : "";
    return i18n.t("{{allButFinalNames}}, and {{finalName}}", { ns: "utils", finalName, allButFinalNames });
  }

  const remainingCount = names.length - maxCount;

  const showCount = remainingCount === 1 ? maxCount - 1 : maxCount;

  if (showCount <= 0) {
    return names
      .slice(0, maxCount)
      .map((name) => formatName(name, internal))
      .join(", ");
  }

  const othersCount = names.length - showCount;
  const shownNames = names
    .slice(0, showCount)
    .map((name) => formatName(name, internal))
    .join(", ");

  return i18n.t("{{shownNames}}, and {{count}} others", { ns: "utils", count: othersCount, shownNames });
};
